// Text arrives as if typed, with a natural rhythm: base + jitter per char, holds after spaces,
// longer after punctuation and newlines, an occasional hesitation; the whole schedule is scaled
// so a long turn never drags. Also: which turn holds focus.
import { reduced } from "./dom.ts";

/**
 * per-character delays in ms, and their total. The text arrives from the server whole, so this is a fake stream: the characters
 * come in token-sized bursts (a few characters at once, the gaps between them the network's tick), a space or a stop holding a
 * little longer, now and then a stall as if the stream had hiccuped, and now and then a run that comes all at once.
 */
export const rhythm = (text: string) => {
  const d = new Array(text.length);
  let total = 0,
    left = 0; // characters left in the burst under way
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    let t;
    if (left > 0) {
      t = 2 + Math.random() * 6;
      left--;
    } // inside a token: nearly at once
    else {
      // the next token: the stream's tick, a little longer after a space, more after a stop
      left = 1 + Math.floor(Math.random() * 5);
      t = 28 + Math.random() * 60;
      if (c === " ") t += 10 + Math.random() * 30;
      else if (c === "," || c === ";" || c === ":") t += 60 + Math.random() * 120;
      else if (c === "." || c === "!" || c === "?") t += 140 + Math.random() * 260;
      else if (c === "\n") t += 200 + Math.random() * 300;
      if (Math.random() < 0.05) t += 200 + Math.random() * 600; // a stall
      if (Math.random() < 0.08) left += 6 + Math.floor(Math.random() * 12); // a run all at once
    }
    d[i] = t;
    total += t;
  }
  return { d, total };
};

/** the factor that fits a rhythm into `maxMs` (1 when it already does) */
export const scaleFor = (total: number, maxMs: number) => (total > maxMs ? maxMs / total : 1);

/**
 * Type `text` into `el`, never taking longer than `maxMs`; `done` when finished. The handle's
 * `cancel()` snaps to the full text at once.
 */
export const typeInto = (el: HTMLElement, text: string, maxMs: number, done?: () => void): { cancel(): void } => {
  if (reduced || !text || maxMs <= 0) {
    el.textContent = text;
    if (done) done();
    return { cancel() {} };
  }
  const { d, total } = rhythm(text);
  const k = scaleFor(total, maxMs);
  const t0 = performance.now();
  let i = 0,
    shown = 0,
    acc = 0,
    last = t0,
    raf = 0,
    alive = true;
  // one text node, the new characters appended to it: a frame with nothing due writes nothing (most frames fall
  // between the bursts), and a write lays out only what grew, not the whole turn again
  const node = el.ownerDocument.createTextNode("");
  el.textContent = "";
  el.appendChild(node);
  el.classList.add("typing");
  const tick = (now: number) => {
    if (!alive) return;
    acc += now - last;
    last = now;
    while (i < text.length && acc >= d[i] * k) {
      acc -= d[i] * k;
      i++;
    }
    if (i > shown) {
      node.appendData(text.slice(shown, i));
      shown = i;
    }
    el.classList.toggle("caret-off", (now - t0) % 1000 >= 500); // the caret's blink (ring.css); unchanged, no write
    if (i < text.length) raf = requestAnimationFrame(tick);
    else {
      el.classList.remove("typing", "caret-off");
      alive = false;
      if (done) done();
    }
  };
  raf = requestAnimationFrame(tick);
  return {
    cancel() {
      if (!alive) return;
      alive = false;
      cancelAnimationFrame(raf);
      el.textContent = text;
      el.classList.remove("typing", "caret-off");
    },
  };
};

/** the message being typed holds focus; the rest ease down */
export const focusMsg = (list: HTMLElement, li: HTMLElement) => {
  list.querySelectorAll("li.msg").forEach((x) => {
    x.classList.toggle("focus", x === li);
    x.classList.toggle("dimmed", x !== li);
  });
};

export const unfocusAll = (list: HTMLElement) =>
  list.querySelectorAll("li.msg").forEach((x) => x.classList.remove("focus", "dimmed"));
